/* eslint-disable max-len */
import Link from 'next/link'
import React from 'react'

export interface FAQItem {
  answer: React.ReactNode
  answerText: string
  category: string
  group?: string | string[]
  id: string
  question: string
  tags: string[]
}

type FAQSourceItem = Omit<FAQItem, 'answer'> & {
  answer?: React.ReactNode
}

const faqSourceItems: FAQSourceItem[] = [
  {
    id: 'what-is-shipandplay',
    question: 'What is Ship&Play?',
    group: 'default',
    category: 'How It Works & Getting Started',
    tags: ['door-to-door', 'luggage', 'sports equipment', 'travel', 'shipping'],
    answerText:
      'Ship&Play is a door-to-door shipping service that helps you travel effortlessly by shipping your luggage, boxes, sports equipment, and family essentials ahead of your trip—or back home at the end of your adventure. We pick up from your home, office, resort, or hotel and deliver directly to your destination. By shipping ahead with Ship&Play, you can skip the stress of hauling heavy bags through airports, waiting at baggage claim, and dealing with lost or delayed luggage.',
    answer: (
      <>
        <p>
          Ship&amp;Play is a door-to-door shipping service that helps you travel effortlessly by
          shipping your luggage, boxes, sports equipment, and family essentials ahead of your
          trip&mdash;or back home at the end of your adventure. We pick up from your home, office,
          resort, or hotel and deliver directly to your destination.
        </p>
        <p>
          By shipping ahead with Ship&amp;Play, you can skip the stress of hauling heavy bags
          through airports, waiting at baggage claim, and dealing with lost or delayed luggage.
        </p>
      </>
    ),
  },
  {
    id: 'create-account',
    question: 'How do I create an account?',
    group: 'default',
    category: 'How It Works & Getting Started',
    tags: ['sign up', 'register', 'log in', 'login', 'profile'],
    answerText:
      'Navigate to ‘Log In’ in the top right corner of your screen. You can either create a new account or sign in to an existing one. Creating an account requires no commitment and only takes a few moments to complete.',
  },
  {
    id: 'free-account',
    question: 'Is it free to create an account?',
    category: 'How It Works & Getting Started',
    tags: ['free', 'sign up', 'registration', 'cost', 'price', 'pricing', 'commitment'],
    answerText:
      'Yes, signing up is completely free! There’s no cost or commitment to create an account.',
  },
  {
    id: 'who-can-use-shipandplay',
    question: 'Who can use Ship&Play?',
    category: 'How It Works & Getting Started',
    tags: ['eligibility', 'travelers', 'families', 'guests', 'customers'],
    answerText:
      "Anyone can use Ship&Play! Whether you're a seasoned adventurer, a first-time traveler, or shipping on behalf of someone else, our service is designed to make travel easier for everyone.",
  },
  {
    id: 'why-ship-instead-of-check',
    question: 'Why should I ship my luggage instead of checking it?',
    category: 'How It Works & Getting Started',
    tags: [
      'baggage fees',
      'cost',
      'price',
      'pricing',
      'airport lines',
      'lost luggage',
      'heavy bags',
      'convenience',
    ],
    answerText:
      'Shipping your luggage lets you avoid airport lines, airline baggage fees, lost-bag risk, and physically carrying heavy bags through airports, trains, and hotels.',
  },
  {
    id: 'is-luggage-shipping-safe',
    question: 'Is luggage shipping safe?',
    category: 'How It Works & Getting Started',
    tags: ['safety', 'tracking', 'insurance', 'handling', 'transfers'],
    answerText:
      'Yes. Luggage shipping is generally safer than airline baggage handling because it reduces transfers, minimizes handling points, and includes tracking and insurance.',
  },
  {
    id: 'book-shipment',
    question: 'How do I book a shipment?',
    group: 'default',
    category: 'How It Works & Getting Started',
    tags: ['reservation', 'pickup', 'delivery', 'travel dates', 'service'],
    answerText:
      'Get started online. Simply enter your pickup and delivery locations, select your travel dates, and choose the service that best fits your needs.',
    answer: (
      <p>
        <a href="/book/ship" className="snplay-link">
          Click here
        </a>{' '}
        to get started. Simply enter your pickup and delivery locations, select your travel dates,
        and choose the service that best fits your needs.
      </p>
    ),
  },
  {
    id: 'after-booking',
    question: 'What happens after I book?',
    group: 'default',
    category: 'How It Works & Getting Started',
    tags: ['confirmation', 'shipping labels', 'instructions', 'next steps'],
    answerText:
      'Once you complete your booking, you’ll receive your shipping labels and step-by-step instructions.',
  },
  {
    id: 'schedule-arrival',
    question: 'How many days in advance should I schedule my luggage to arrive?',
    category: 'How It Works & Getting Started',
    tags: ['arrival date', 'schedule', 'business day', 'timing', 'planning'],
    answerText:
      'We suggest scheduling your luggage to arrive at least one business day prior to your arrival. For example, if you’re planning to arrive on Friday, plan to have your bags delivered by the Thursday before.',
  },
  {
    id: 'one-way-vs-round-trip',
    question: 'What’s the difference between one-way and round-trip shipping?',
    category: 'How It Works & Getting Started',
    tags: ['one way', 'round trip', 'return', 'cost', 'price', 'pricing', 'shipping direction'],
    answerText:
      'One-way shipping means your luggage is shipped from your starting point to your destination. Round-trip shipping includes shipping your bags to your destination and then back to your home or original location. There is no cost difference between two one-way shipments and a round-trip booking.',
  },
  {
    id: 'other-items',
    question: 'What other items can I ship using Ship&Play?',
    category: 'How It Works & Getting Started',
    tags: ['boxes', 'sports equipment', 'family essentials', 'specialty items', 'group shipment'],
    answerText:
      'Travel completely bag-free by shipping your boxes, sports equipment, and family essentials along with your luggage. If you’re interested in shipping specialty items or arranging group or event shipments, please contact our team so we can explore how to best accommodate your needs.',
    answer: (
      <>
        <p>
          Travel completely bag-free by shipping your boxes, sports equipment, and family essentials
          along with your luggage.
        </p>
        <p>
          If you&rsquo;re interested in shipping specialty items or arranging group or event
          shipments, please{' '}
          <Link href="/contact" className="snplay-link">
            contact our team
          </Link>{' '}
          so we can explore how to best accommodate your needs.
        </p>
      </>
    ),
  },
  {
    id: 'shipping-destinations',
    question: 'Where can I ship my luggage?',
    category: 'How It Works & Getting Started',
    tags: ['destination', 'hotel', 'cruise port', 'po box', 'service area'],
    answerText:
      'You can ship luggage from home to hotel, hotel to home, between hotels, to resorts, cruise ports, and internationally (subject to customs regulations) – or any physical destination of your choosing, with the exception of P.O. boxes, airports, and countries we don’t currently service.',
  },
  {
    id: 'allowed-bags-containers',
    question: 'What types of bags/containers are allowed?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['suitcase', 'duffel bag', 'hard case', 'box', 'sports equipment'],
    answerText:
      'Pack your luggage just like you would for the airlines, in a suitcase or duffel bag. You may also package your luggage in a box, but it is not required. Sports equipment, like golf clubs and skis, must be packaged in a soft travel bag, hard case, or box. Bikes must be boxed.',
    answer: (
      <>
        <p>
          Pack your luggage just like you would for the airlines, in a suitcase or duffel bag. You
          may also package your luggage in a box, but it is not required.
        </p>
        <p>
          Sports equipment, like golf clubs and skis, must be packaged in a soft travel bag, hard
          case, or box. Bikes must be boxed.
        </p>
      </>
    ),
  },
  {
    id: 'pack-luggage',
    question: 'How should I pack my luggage?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['packing tips', 'luggage', 'sports equipment', 'travel bag', 'protect gear'],
    answerText:
      'See our packing guides for more information on packing luggage and sports equipment.',
    answer: (
      <p>
        <Link href="/pack" className="snplay-link">
          Click here
        </Link>{' '}
        for more information on packing luggage and sports equipment.
      </p>
    ),
  },
  {
    id: 'lock-domestic-shipment',
    question: 'Can I lock my domestic shipment?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['lock', 'domestic', 'united states', 'security', 'tsa lock'],
    answerText: 'If you’re shipping domestically within the U.S., your bag may be locked.',
  },
  {
    id: 'lock-international-shipment',
    question: 'Can I lock my international shipment?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['lock', 'customs', 'international', 'inspection', 'shipment protection'],
    answerText:
      'If you’re shipping internationally, locks are NOT allowed due to customs inspections. Locked international shipments can cause delays and will void our shipment protection policy.',
  },
  {
    id: 'size-weight-limits',
    question: 'Are there size or weight limits?',
    category: 'Packing & Preparing Your Shipment',
    tags: [
      'dimensions',
      'weight limit',
      'bag size',
      'custom quote',
      'cost',
      'price',
      'pricing',
      'oversized',
    ],
    answerText:
      'Yes. Please identify your bag’s maximum dimensions (length + width + height) and weight to determine which bag size category it fits into. If your shipment exceeds the size or weight limits for that category, please contact our team—we’ll be happy to provide a custom quote for your shipment.',
    answer: (
      <p>
        Yes. Please identify your bag&rsquo;s maximum dimensions (length + width + height) and
        weight to determine which bag size category it fits into, listed{' '}
        <Link href="/what-can-i-ship" className="snplay-link">
          here
        </Link>
        . If your shipment exceeds the size or weight limits for that category, please{' '}
        <Link href="/contact" className="snplay-link">
          contact our team
        </Link>
        &mdash;we&rsquo;ll be happy to provide a custom quote for your shipment.
      </p>
    ),
  },
  {
    id: 'overweight-bag',
    question: 'What happens if my bag is overweight?',
    category: 'Packing & Preparing Your Shipment',
    tags: [
      'overweight',
      'extra fee',
      'cost',
      'price',
      'pricing',
      'delay',
      'bag size',
      'weight limit',
    ],
    answerText:
      'If your bag weighs more than the limit for your chosen size, there could be extra fees and possible delays. To keep things running smoothly, we recommend sizing up if you’re unsure—better safe than sorry!',
  },
  {
    id: 'cannot-weigh-bag',
    question: 'What if I can’t weigh my bag?',
    category: 'Packing & Preparing Your Shipment',
    tags: [
      'estimate weight',
      'scale',
      'additional fees',
      'cost',
      'price',
      'pricing',
      'fedex',
      'ups',
      'dhl',
    ],
    answerText:
      'Give your best estimate when booking, or head to a local FedEx, UPS, or DHL to have your bag weighed. If the weight is significantly heavier than expected, additional fees may apply.',
  },
  {
    id: 'attach-shipping-labels',
    question: 'How do I attach the shipping labels?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['label', 'packing tape', 'barcode', 'luggage tag', 'fly tag'],
    answerText:
      "If you book at least 3 business days in advance, and your shipment originates in the U.S, we'll send labels (in tags) to your chosen address. If you don’t meet that criteria, and are using a hard travel case or box, attach your label with clear packing tape, making sure the barcode is visible and secure. If your bag doesn’t have a hard surface, you’ll need to place the label on a luggage baggage tag (also known as a fly tag), which you can pick up from your local FedEx, UPS, or DHL location, and attach it to the handle. Don’t forget to put a second copy of your shipping label in your bag, too.",
  },
  {
    id: 'no-printer',
    question: 'What if I don’t have a printer?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['printer', 'print label', 'email label', 'mailed labels', 'carrier'],
    answerText:
      "If you book at least 3 business days in advance, and your shipment originates in the U.S, we'll send labels (in tags) to your chosen address. If you don’t meet that criteria, you can email the shipping label to your local FedEx, UPS, or DHL for printing.",
  },
  {
    id: 'packaging-provided',
    question: 'Does Ship&Play provide packaging?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['packaging', 'hard case', 'soft bag', 'box', 'supplies'],
    answerText:
      'We don’t supply packaging, but no worries—you can simply use your own suitcase, duffel bag, hard case, soft travel bag, or box to ship your belongings.',
  },
  {
    id: 'need-box',
    question: 'Do I need a box?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['box', 'airline approved', 'hard case', 'soft travel bag', 'duffel'],
    answerText:
      "Nope, no box needed! If you're shipping luggage or sports equipment in an airline-approved bag or case, you're good to go.",
  },
  {
    id: 'get-box',
    question: 'Where do I get a box?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['buy box', 'cost', 'price', 'pricing', 'ups store', 'fedex', 'suitcase', 'duffel bag'],
    answerText:
      "If you’re shipping in a suitcase or duffel bag, you don't need a box—that's the beauty of our service. However, if you need a box, you can usually buy one at your local UPS or FedEx store for a small fee (prices vary by location).",
  },
  {
    id: 'international-packing',
    question: 'Do I have to pack my bag differently if I am shipping internationally?',
    category: 'Packing & Preparing Your Shipment',
    tags: ['customs', 'tsa lock', 'international packing', 'prohibited items', 'price tags'],
    answerText:
      'For international shipments, no locks are allowed, including TSA locks, since customs may need to inspect your package. Here’s what you can include in your luggage: worn clothing (no price tags), footwear, headwear, and undergarments. Some things are off-limits, like toiletries (soap, shampoo, toothpaste), medications, pressurized containers (aerosol cans or CO2 cartridges), cash or negotiable items, electronics, batteries, alcohol or anything containing alcohol (including perfume and cologne), tobacco, firearms and ammunition, and food, vitamins, or other consumables. Before shipping, please remove all price tags. For the full list of prohibited items, check out our Terms of Use.',
    answer: (
      <>
        <p>
          For international shipments, no locks are allowed, including TSA locks, since customs may
          need to inspect your package.
        </p>
        <p>
          Here&rsquo;s what you can include in your luggage: worn clothing (no price tags),
          footwear, headwear, and undergarments.
        </p>
        <p>
          Some things are off-limits, like toiletries (soap, shampoo, toothpaste), medications,
          pressurized containers (aerosol cans or CO2 cartridges), cash or negotiable items,
          electronics, batteries, alcohol or anything containing alcohol (including perfume and
          cologne), tobacco, firearms and ammunition, and food, vitamins, or other consumables.
        </p>
        <p>
          Before shipping, please remove all price tags. For the full list of prohibited items,
          check out our{' '}
          <Link href="/terms" className="snplay-link">
            Terms of Use
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    id: 'pickup-carrier',
    question: 'Who will be picking up my shipment?',
    category: 'Pickup & Delivery',
    tags: ['carrier', 'fedex', 'ups', 'dhl', 'pickup'],
    answerText:
      'One of our trusted logistics partners, either FedEx, UPS, or DHL, will pick up your luggage. Your shipping label will indicate which carrier will handle your pickup.',
  },
  {
    id: 'home-pickup',
    question: 'Can I get my bags picked up from my home?',
    category: 'Pickup & Delivery',
    tags: ['home pickup', 'office', 'hotel', 'cruise port', 'drop off'],
    answerText:
      'Yes! We can pick up your luggage from your home, office, resort, hotel, rental—even a cruise port. Or you can simply drop it off at your nearest carrier store for added convenience.',
  },
  {
    id: 'drop-off-fee',
    question: 'Is there a fee for dropping off my luggage?',
    category: 'Pickup & Delivery',
    tags: [
      'drop-off fee',
      'cost',
      'price',
      'pricing',
      'fedex office',
      'ups store',
      'handling fee',
      'third party',
    ],
    answerText:
      'FedEx Offices and UPS Stores do not charge fees to drop off. However, some third-party pickup and drop-off locations may charge package handling fees. These fees are separate from our service and can vary depending on the location. To avoid surprises, we recommend calling ahead to check if any fees apply.',
  },
  {
    id: 'choose-drop-off-location',
    question: 'Can I choose my carrier drop-off location?',
    category: 'Pickup & Delivery',
    tags: ['drop-off location', 'carrier store', 'fedex', 'ups', 'dhl'],
    answerText:
      'While we assign your carrier (FedEx, UPS, or DHL) when you book, you’re free to drop off your shipment at any local location serviced by that carrier. Use our drop-off locator to find a location near you.',
    answer: (
      <p>
        While we assign your carrier (FedEx, UPS, or DHL) when you book, you&rsquo;re free to drop
        off your shipment at any local location serviced by that carrier.{' '}
        <Link href="/drop-off-locator" className="snplay-link">
          Click here
        </Link>{' '}
        to find a drop-off location near you.
      </p>
    ),
  },
  {
    id: 'home-during-pickup',
    question: 'Do I have to be home when my bags are picked up?',
    category: 'Pickup & Delivery',
    tags: ['unattended pickup', 'pickup window', 'front door', 'drop off', 'carrier'],
    answerText:
      'You don’t need to be home for pickup. Just leave your securely packed and labeled shipment outside your front door during the pickup window, at your discretion. If you don’t want to leave it unattended and can’t be home, you can drop it off at a local FedEx, UPS, or DHL store—just make sure it matches the carrier listed on your shipping label.',
  },
  {
    id: 'cancel-order',
    question: 'Can I cancel my order?',
    category: 'Pickup & Delivery',
    tags: ['cancel', 'cancellation', 'support team', 'pickup', 'drop off'],
    answerText:
      'Yes, you can cancel your order at any point prior to your scheduled pickup or drop off. Please contact our support team to request a cancellation.',
    answer: (
      <p>
        Yes, you can cancel your order at any point prior to your scheduled pickup or drop off.
        Please{' '}
        <Link href="/contact" className="snplay-link">
          contact our support team
        </Link>{' '}
        to request a cancellation.
      </p>
    ),
  },
  {
    id: 'change-order',
    question: 'Can I make changes to my order?',
    category: 'Pickup & Delivery',
    tags: ['modify', 'update order', 'support team', 'pickup', 'drop off'],
    answerText:
      'Yes, you can update your order at any point prior to your scheduled pickup or drop off. Please contact our support team to request a change.',
    answer: (
      <p>
        Yes, you can update your order at any point prior to your scheduled pickup or drop off.
        Please{' '}
        <Link href="/contact" className="snplay-link">
          contact our support team
        </Link>{' '}
        to request a change.
      </p>
    ),
  },
  {
    id: 'delivery-time',
    question: 'How long does delivery take?',
    category: 'Pickup & Delivery',
    tags: ['transit time', 'ground', 'expedited', 'next day', 'business days'],
    answerText:
      'Delivery times vary depending on the service level you choose. Ground shipments typically arrive within 2 to 6 business days, depending on the route, while expedited options can get your luggage there even faster—some even as quickly as the next business day. After pickup, you’ll receive tracking updates so you can monitor your shipment every step of the way.',
  },
  {
    id: 'delivery-signature',
    question: 'Is a signature required upon delivery?',
    category: 'Pickup & Delivery',
    tags: ['signature', 'driver', 'secure location', 'receiving area', 'supervised'],
    answerText:
      'A signature is not required, but it’s ultimately up to the driver’s discretion. If the driver considers the delivery area secure or your destination has a shipping/receiving area, your shipment will be delivered without a signature. If this is a concern, we recommend shipping to a supervised location, like a business, hotel, resort, friend or family member, or carrier store (fees may apply).',
  },
  {
    id: 'weekend-delivery',
    question: 'Does Ship&Play deliver on weekends?',
    category: 'Pickup & Delivery',
    tags: ['weekend', 'saturday', 'sunday', 'business days', 'delivery schedule'],
    answerText:
      'Deliveries can only be scheduled Monday through Friday to ensure your shipment is handled during regular business days when all of our partnered carriers are operating. That said, depending on the route, your luggage might still arrive on a weekend.',
  },
  {
    id: 'destination-acceptance',
    question:
      'Do I need to confirm that the resort, rental home, or hotel will accept my shipment?',
    category: 'Pickup & Delivery',
    tags: ['hotel', 'resort', 'rental home', 'accept shipment', 'arrival date'],
    answerText:
      'Before placing your order, we do recommend reaching out to your destination to let them know about your shipment and your expected arrival date.',
  },
  {
    id: 'destination-holding-fees',
    question: 'Do hotels and resorts charge to receive/hold my luggage?',
    category: 'Pickup & Delivery',
    tags: [
      'hotel fee',
      'resort fee',
      'holding fee',
      'receiving fee',
      'cost',
      'price',
      'pricing',
      'destination',
    ],
    answerText:
      'Policies vary by location. Some properties may charge a fee for receiving or holding shipments, so it’s best to check directly with them beforehand.',
  },
  {
    id: 'different-return-address',
    question:
      'Can I have my bag returned to a different address than the one it was originally shipped from?',
    category: 'Pickup & Delivery',
    tags: ['return address', 'add another leg', 'multi-stop', 'round trip', 'booking'],
    answerText: 'That’s easy! Just be sure to select ‘Add Another Leg’ when booking.',
  },
  {
    id: 'track-shipment',
    question: 'How do I track my shipment?',
    group: 'default',
    category: 'Shipping & Tracking',
    tags: ['shipment status', 'delivery status', 'email alerts', 'text alerts', 'updates'],
    answerText:
      'We’ll keep you posted with email updates and optional text alerts when your shipment is picked up, out for delivery, and delivered. You’ll also find a tracking link in your confirmation email. To track through your account, visit ShipPlay.com, log in using the link in the top-right corner, go to My Account > Shipments, and select the shipment you’d like to track.',
    answer: (
      <>
        <p>
          We&rsquo;ll keep you posted with email updates and optional text alerts when your shipment
          is picked up, out for delivery, and delivered. You&rsquo;ll also find a tracking link in
          your confirmation email. To track through your account, simply:
        </p>
        <ol className="snplay:list-decimal snplay:space-y-2 snplay:pl-6">
          <li>
            Visit{' '}
            <a href="https://www.shipplay.com" className="snplay-link">
              www.ShipPlay.com
            </a>
          </li>
          <li>Log in (top-right corner)</li>
          <li>Go to My Account &gt; Shipments</li>
          <li>Select the shipment you&rsquo;d like to track</li>
        </ol>
      </>
    ),
  },
  {
    id: 'delivery-notifications',
    question: 'Will I get notifications for delivery updates?',
    category: 'Shipping & Tracking',
    tags: ['notifications', 'email', 'text message', 'real-time tracking', 'delivery update'],
    answerText:
      'Yes! You’ll receive real-time tracking notifications via email so you always know where your shipment is and when to expect delivery. You can opt in to receive notifications via text as well.',
  },
  {
    id: 'delayed-or-lost',
    question: 'What if my shipment is delayed or lost?',
    category: 'Shipping & Tracking',
    tags: ['delay', 'lost shipment', 'missing bag', 'investigation', 'support team'],
    answerText:
      'Although delays and losses are rare, if we suspect either might have occurred, our support team will reach out to you. We’ll promptly investigate and work to resolve the issue as quickly as possible.',
  },
  {
    id: 'international-service',
    question: 'Do you ship internationally?',
    category: 'Shipping & Tracking',
    tags: ['international', 'countries', 'territories', 'customs', 'worldwide'],
    answerText:
      'We service 180 countries and territories, and handle all the tricky customs paperwork.',
  },
  {
    id: 'luggage-shipping-cost',
    question: 'How much does it cost to ship luggage?',
    category: 'Pricing, Insurance & Claims',
    tags: ['cost', 'price', 'pricing', 'quote', 'shipping distance', 'service level', 'estimate'],
    answerText:
      'The cost to ship luggage varies depending on factors like your shipping distance and the service level you choose. To get an exact quote, simply enter your pickup and delivery locations on our website or contact our support team for personalized assistance.',
    answer: (
      <p>
        The cost to ship luggage varies depending on factors like your shipping distance and the
        service level you choose. To get an exact quote, simply enter your pickup and delivery
        locations on our website or{' '}
        <Link href="/contact" className="snplay-link">
          contact our support team
        </Link>{' '}
        for personalized assistance.
      </p>
    ),
  },
  {
    id: 'insurance-included',
    question: 'Is insurance included? Can I add more coverage?',
    category: 'Pricing, Insurance & Claims',
    tags: [
      'insurance',
      'coverage',
      'declared value',
      'protection',
      'cost',
      'price',
      'pricing',
      'add-on',
    ],
    answerText:
      'Luggage shipments come with $500 of insurance. Need more coverage? You can add up to $7,500 for a small fee. We recommend choosing an amount that reflects the full value of your belongings and the bag it’s packed in.',
  },
  {
    id: 'insurance-coverage',
    question: 'What is covered under insurance?',
    category: 'Pricing, Insurance & Claims',
    tags: ['insurance coverage', 'loss', 'physical damage', 'travel case', 'exclusions'],
    answerText:
      'Ship&Play’s shipment insurance protects against complete loss of your shipped item or against any physical damage to qualified items that has occurred while in transit. For items not shipped in a box, the outside casing is considered the shipping container and protruding parts such as wheels, straps, pockets, handles, or other items attached to a travel case or bag will not be covered.',
  },
  {
    id: 'file-claim',
    question: 'How do I file a damage/missing item claim?',
    category: 'Pricing, Insurance & Claims',
    tags: ['claim form', 'damage', 'missing item', 'claims representative', 'insurance'],
    answerText:
      'In order to file a damage or missing items claim, a claims form must be submitted to a claims representative. Claims requirements are outlined on our claims form.',
    answer: (
      <p>
        In order to file a damage or missing items claim, a claims form must be submitted to a
        claims representative. Claims requirements are outlined on our{' '}
        <Link href="/claims" className="snplay-link">
          claims form
        </Link>
        .
      </p>
    ),
  },
  {
    id: 'claim-resolution-time',
    question: 'How long does it take to resolve a claim?',
    category: 'Pricing, Insurance & Claims',
    tags: ['claim status', 'claims specialist', 'business days', 'approval', 'denial'],
    answerText:
      'A claims specialist will review and file your claim within 1-2 business days. If additional information is needed, a claims specialist will contact you by phone or email. Otherwise, you will be made aware of your claim being filed. Once your claim has been reviewed and filed, you can expect to receive an approval or denial within 14 business days. Once the claim has been filed, it cannot be altered.',
    answer: (
      <>
        <p>
          A claims specialist will review and file your claim within 1-2 business days. If
          additional information is needed, a claims specialist will contact you by phone or email.
          Otherwise, you will be made aware of your claim being filed.
        </p>
        <p>
          Once your claim has been reviewed and filed, you can expect to receive an approval or
          denial within 14 business days. Once the claim has been filed, it cannot be altered.
        </p>
      </>
    ),
  },
  {
    id: 'specialty-oversized-items',
    question: 'Can I ship specialty or oversized items?',
    category: 'Custom Orders',
    tags: ['specialty item', 'oversized', 'custom order', 'luggage', 'contact us'],
    answerText:
      'Yes! You can ship specialty items along with your luggage. To get started, just contact our team—we’re happy to help arrange those shipments.',
    answer: (
      <p>
        Yes! You can ship specialty items along with your luggage. To get started, just{' '}
        <Link href="/contact" className="snplay-link">
          contact our team
        </Link>
        &mdash;we&rsquo;re happy to help arrange those shipments.
      </p>
    ),
  },
  {
    id: 'custom-quote',
    question: 'How do I get a custom quote for special shipments?',
    category: 'Custom Orders',
    tags: [
      'custom quote',
      'specialty shipment',
      'oversized',
      'personalized price',
      'cost',
      'price',
      'pricing',
      'shipping plan',
    ],
    answerText:
      'To get a custom quote for specialty or oversized shipments, please contact our support team with your shipment details. We’ll work with you to provide a personalized price and shipping plan.',
    answer: (
      <p>
        To get a custom quote for specialty or oversized shipments, please{' '}
        <Link href="/contact" className="snplay-link">
          contact our support team
        </Link>{' '}
        with your shipment details. We&rsquo;ll work with you to provide a personalized price and
        shipping plan.
      </p>
    ),
  },
  {
    id: 'items-cannot-ship',
    question: 'Are there items you cannot ship?',
    category: 'Custom Orders',
    tags: ['prohibited', 'hazardous materials', 'perishable', 'restricted', 'shipping policy'],
    answerText:
      'Yes, there are certain items we cannot ship for safety and legal reasons. These include hazardous materials, perishable goods, and restricted items as outlined in our shipping policy. If you’re unsure whether your item qualifies, please contact us before booking.',
    answer: (
      <p>
        Yes, there are certain items we cannot ship for safety and legal reasons. These include
        hazardous materials, perishable goods, and restricted items as outlined in our shipping
        policy. If you&rsquo;re unsure whether your item qualifies, please{' '}
        <Link href="/contact" className="snplay-link">
          contact us
        </Link>{' '}
        before booking.
      </p>
    ),
  },
  {
    id: 'contact-support',
    question: 'How do I contact customer support?',
    category: 'Support',
    tags: ['contact', 'customer service', 'help', 'phone', 'email'],
    answerText: 'Use the contact page to reach our customer support team.',
    answer: (
      <p>
        <Link href="/contact" className="snplay-link">
          Click here
        </Link>{' '}
        to contact us.
      </p>
    ),
  },
]

export const faqItems: FAQItem[] = faqSourceItems.map((item) => ({
  ...item,
  answer: item.answer ?? <p>{item.answerText}</p>,
}))
